import { DEBOUNCE } from '@/core/core.constants';
import { sqInvestigateStore, sqManualSignalStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { generateFormula } from '@/tools/manualSignal/manualSignal.utilities.service';
import { cancelPreviewSeries, generatePreviewSeries, removePreviewSeries } from '@/trendData/trend.actions';
import { PREVIEW_ID } from '@/trendData/trendData.constants';
import { asyncDebounce } from '@/utilities/asyncDebounce';
import type { InputTableSort } from '@/formbuilder/InputTableFormComponent.molecule';
import type { ManualSample } from '@/tools/manualSignal/manualSignal.constants';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export function setInterpolationMethod(interpolationMethod: string) {
  manualSignalDispatch('MANUAL_SIGNAL_SET_INTERPOLATION_METHOD', {
    interpolationMethod,
  });
}

type SetMaximumInterpolationArg = {
  value?: number | string;
  units?: ValueWithUnitsItem['units'];
  valid?: boolean;
};
export function setMaximumInterpolation(maximumInterpolation: SetMaximumInterpolationArg) {
  // Validate input before dispatching to prevent invalid state
  if (
    maximumInterpolation.valid === false ||
    maximumInterpolation.value == null ||
    maximumInterpolation.units == null ||
    Number.isNaN(Number(maximumInterpolation.value))
  ) {
    return;
  }

  manualSignalDispatch('MANUAL_SIGNAL_SET_MAXIMUM_INTERPOLATION', {
    maximumInterpolation: {
      value: Number(maximumInterpolation.value),
      units: maximumInterpolation.units,
    } satisfies ValueWithUnitsItem,
  });
}

export function setUom(uom: string) {
  manualSignalDispatch('MANUAL_SIGNAL_SET_UOM', { uom });
}

export function addSample(sample: ManualSample) {
  manualSignalDispatch('MANUAL_SIGNAL_ADD_SAMPLE', { sample });
}

export function removeSample(id: string) {
  manualSignalDispatch('MANUAL_SIGNAL_REMOVE_SAMPLE', { id });
}

export function updateSample(sample: ManualSample) {
  manualSignalDispatch('MANUAL_SIGNAL_UPDATE_SAMPLE', { sample });
}

export function setEditingSampleId(editingSampleId: string | undefined) {
  manualSignalDispatch('MANUAL_SIGNAL_SET_EDITING_SAMPLE_ID', { editingSampleId });
}

export function setSort(sort: InputTableSort) {
  manualSignalDispatch('MANUAL_SIGNAL_SET_SORT', { sort });
}

export function setColor(color: string): void {
  manualSignalDispatch('MANUAL_SIGNAL_SET_COLOR', { color });
}

const triggerManualSignalPreviewDebounced = asyncDebounce(generateManualSignalPreview, DEBOUNCE.PREVIEW);

export function manualSignalDispatch(handler: string, payload: any): void {
  flux.dispatch(handler, payload);
  triggerManualSignalPreviewDebounced();
}

export function generateManualSignalPreview() {
  removePreviewSeries();
  const { samples, id, color } = sqManualSignalStore;
  if (sqInvestigateStore.activeTool === TREND_TOOLS.MANUAL_SIGNAL && samples.length > 0) {
    const formula = generateFormula();
    return generatePreviewSeries(formula, {}, id ? id : PREVIEW_ID, color).catch(() => removePreviewSeries());
  } else {
    cancelPreviewSeries();
    return Promise.resolve();
  }
}
